import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { BUSINESS, PRESS_FEATURES, activePress } from "@/lib/site-data";

export const Route = createFileRoute("/press")({
  head: () => ({
    meta: [
      { title: "Press & Podcasts — Lehi Trades SEO" },
      {
        name: "description",
        content:
          "Podcast appearances, guest columns, and press features by the Lehi Trades SEO team on trades marketing, SEO, and GEO.",
      },
      { property: "og:title", content: "Press & Podcasts — Lehi Trades SEO" },
      {
        property: "og:description",
        content:
          "James & Will from Lehi Trades SEO on podcasts and trade publications — book us as a guest.",
      },
      { property: "og:url", content: "https://lehitradessaeo.com/press" },
    ],
    links: [{ rel: "canonical", href: "https://lehitradessaeo.com/press" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Press & Podcasts",
          url: "https://lehitradessaeo.com/press",
          isPartOf: { "@id": "https://lehitradessaeo.com/#website" },
          about: { "@id": "https://lehitradessaeo.com/#organization" },
        }),
      },
    ],
  }),
  component: PressPage,
});

function PressPage() {
  const items = activePress();
  const podcasts = items.filter((p) => p.kind === "podcast");
  const columns = items.filter((p) => p.kind === "column");
  const features = items.filter((p) => p.kind === "feature");

  return (
    <main className="mx-auto max-w-3xl px-6 py-20">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Digital PR</p>
      <h1 className="mt-3 font-display text-4xl tracking-tight md:text-5xl">
        Press, podcasts & guest columns
      </h1>
      <p className="mt-4 text-lg text-muted-foreground">
        Where the {BUSINESS.name} team shows up off our own site — podcast guest
        spots, tactical guest columns, and trade-press features.
      </p>

      {items.length === 0 ? (
        <section className="mt-10 rounded-2xl border border-border bg-card p-8">
          <h2 className="font-display text-xl tracking-tight">
            Booking podcasts and guest columns now
          </h2>
          <p className="mt-2 text-muted-foreground">
            We're actively booking podcast appearances and tactical guest
            columns for home-service publications. If you run a trade-focused
            show or outlet, we'd love to talk shop.
          </p>
          <p className="mt-4 text-sm text-muted-foreground">
            Pitch topics we cover: city-page architecture, GEO/AI Overviews,
            Local Service Ads economics, GBP review velocity, contractor lead
            economics in 2026.
          </p>
          <div className="mt-8">
            <GuestBookingForm />
          </div>
        </section>
      ) : (
        <div className="mt-10 space-y-12">
          {podcasts.length > 0 && (
            <PressSection title="Podcast appearances" items={podcasts} showHost />
          )}
          {columns.length > 0 && <PressSection title="Guest columns" items={columns} />}
          {features.length > 0 && <PressSection title="Press features" items={features} />}

          <section className="rounded-2xl border border-border bg-card p-8">
            <h2 className="font-display text-xl tracking-tight">Book us as a guest</h2>
            <p className="mt-2 text-muted-foreground">
              Available for podcast guest spots and guest columns on trades
              marketing, SEO, Local Service Ads, and Generative Engine
              Optimization (GEO).
            </p>
            <div className="mt-6">
              <GuestBookingForm />
            </div>
          </section>
        </div>
      )}

      {/* Internal note: not rendered to users. */}
      {PRESS_FEATURES.length === 0 && (
        <p className="mt-12 text-xs text-muted-foreground">
          Editor note: add entries in <code>PRESS_FEATURES</code> in
          <code> src/lib/site-data.ts</code> as podcasts and columns go live.
        </p>
      )}
    </main>
  );
}

function PressSection({
  title,
  items,
  showHost = false,
}: {
  title: string;
  items: ReturnType<typeof activePress>;
  showHost?: boolean;
}) {
  return (
    <section>
      <h2 className="font-display text-2xl tracking-tight">{title}</h2>
      <ul className="mt-4 divide-y divide-border rounded-2xl border border-border bg-card">
        {items.map((p) => (
          <li key={`${p.outlet}-${p.title}`} className="p-5">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              {p.outlet}
              {showHost && p.host ? ` · ${p.host}` : ""}
              {" · "}
              {p.publishedAt}
            </p>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1 block font-display text-lg tracking-tight hover:underline"
            >
              {p.title}
            </a>
            <p className="mt-1 text-sm text-muted-foreground">{p.blurb}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}

function GuestBookingForm() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (submitted) {
    return (
      <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
        <h3 className="font-display text-xl tracking-tight">Request sent.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Thanks — we'll reply from {BUSINESS.email} within two business days
          to confirm a recording date.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (submitting) return;
        setError(null);
        setSubmitting(true);
        const fd = new FormData(e.currentTarget);
        try {
          const res = await fetch("/api/public/guest-booking", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              podcastName: String(fd.get("podcastName") || ""),
              host: String(fd.get("host") || ""),
              audience: String(fd.get("audience") || ""),
              preferredDates: String(fd.get("preferredDates") || ""),
              email: String(fd.get("email") || ""),
              notes: String(fd.get("notes") || ""),
            }),
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(data?.error || "Something went wrong");
          }
          setSubmitted(true);
        } catch (err) {
          setError(err instanceof Error ? err.message : "Something went wrong");
        } finally {
          setSubmitting(false);
        }
      }}
      className="space-y-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium">Podcast / outlet</span>
          <input
            required
            name="podcastName"
            maxLength={160}
            className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Host (optional)</span>
          <input
            name="host"
            maxLength={120}
            className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-sm font-medium">Audience size & focus</span>
        <input
          required
          name="audience"
          maxLength={200}
          placeholder="e.g. 8k downloads/episode, contractors & trades owners"
          className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
        />
      </label>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium">Preferred recording dates</span>
          <input
            required
            name="preferredDates"
            maxLength={200}
            placeholder="e.g. Weeks of Aug 4 or Aug 18"
            className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium">Contact email</span>
          <input
            required
            name="email"
            type="email"
            maxLength={200}
            className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-sm font-medium">Topic ideas (optional)</span>
        <textarea
          name="notes"
          rows={4}
          maxLength={4000}
          className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
        />
      </label>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <button
        type="submit"
        disabled={submitting}
        className="rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
      >
        {submitting ? "Sending…" : "Book us as a guest"}
      </button>
    </form>
  );
}